import React, { useEffect, useState } from 'react';
import { ManagedKeyForm } from './ManagedKeyForm';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { classNames } from '../../utilities/styleUtils';

interface AddAccountDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (accountId: number) => void;
}

/**
 * Modal dialog wrapper for the account creation flow
 *
 * Features:
 * - Modal overlay with semi-transparent background
 * - Dialog header with title "Add Snowflake Account"
 * - Close button (X in top right)
 * - Contains the ManagedKeyForm component
 * - Handles dialog open/close state
 * - Prevents closing during critical operations
 * - Responsive (full screen on mobile, dialog on desktop)
 *
 * @param isOpen - Whether dialog is open
 * @param onClose - Callback to close dialog
 * @param onSuccess - Callback when account is successfully added
 */
export function AddAccountDialog({ isOpen, onClose, onSuccess }: AddAccountDialogProps) {
  const [isProcessing, setIsProcessing] = useState(false);

  // Prevent body scroll when dialog is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle ESC key to close dialog
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isProcessing) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isProcessing, onClose]);

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  const handleSuccess = (accountId: number) => {
    setIsProcessing(false);
    onSuccess(accountId);
  };

  const handleCancel = () => {
    setIsProcessing(false);
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Dialog Container */}
      <div className="flex min-h-full items-center justify-center p-4 sm:p-6 md:p-8">
        {/* Dialog Panel */}
        <div
          className={classNames(
            'relative w-full max-w-3xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all',
            'max-h-[90vh] flex flex-col',
          )}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h2 className="text-xl font-semibold text-gray-900">Add Snowflake Account</h2>
            <button
              type="button"
              className={classNames(
                'rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-dataops-primary-light-blue focus:ring-offset-2',
                isProcessing ? 'cursor-not-allowed opacity-50' : '',
              )}
              onClick={handleClose}
              disabled={isProcessing}
              aria-label="Close dialog"
            >
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <ManagedKeyForm onSuccess={handleSuccess} onCancel={handleCancel} />
          </div>
        </div>
      </div>
    </div>
  );
}
